import { atom } from 'jotai';
import request from 'lib/request';
import type {
  SourceEntry,
  SourcesResponse,
  ClosingDatesResponse,
  StripPriceData,
} from './types';

export const sourcesAtom = atom<SourceEntry[]>([]);
export const sourcesLoadingAtom = atom(false);

export const closingDatesAtom = atom<string[]>([]);
export const closingDatesLoadingAtom = atom(false);
export const closingDatesHasMoreAtom = atom(false);

export const selectedSourceAtom = atom<string | null>(null);
export const selectedDateAtom = atom<string | null>(null);

export const stripPriceDataAtom = atom<StripPriceData | null>(null);
export const stripPriceLoadingAtom = atom(false);

const CLOSING_DATES_LIMIT = 25;

export const fetchSourcesAtom = atom(null, async (_get, set) => {
  set(sourcesLoadingAtom, true);
  try {
    const response = await request.get<SourcesResponse>(
      '/queries/strip_prices/sources'
    );
    if ('error' in response) return;

    const sources = response.sources ?? [];
    set(sourcesAtom, sources);

    if (sources.length > 0) {
      set(selectedSourceAtom, sources[0].source);
    }
  } finally {
    set(sourcesLoadingAtom, false);
  }
});

export const fetchClosingDatesAtom = atom(
  null,
  async (_get, set, source: string) => {
    set(closingDatesLoadingAtom, true);
    try {
      const response = await request.get<ClosingDatesResponse>(
        `/queries/strip_prices/closing_dates?source=${encodeURIComponent(
          source
        )}&limit=${CLOSING_DATES_LIMIT}`
      );
      if ('error' in response) {
        set(selectedDateAtom, null);
        set(closingDatesAtom, []);
        return;
      }

      const dates = response.closingDates ?? [];
      set(closingDatesAtom, dates);
      set(closingDatesHasMoreAtom, dates.length >= CLOSING_DATES_LIMIT);
      set(selectedDateAtom, dates[0] ?? null);
    } finally {
      set(closingDatesLoadingAtom, false);
    }
  }
);

export const fetchMoreClosingDatesAtom = atom(null, async (get, set) => {
  const source = get(selectedSourceAtom);
  if (!source || !get(closingDatesHasMoreAtom) || get(closingDatesLoadingAtom))
    return;

  const existing = get(closingDatesAtom);

  try {
    const response = await request.get<ClosingDatesResponse>(
      `/queries/strip_prices/closing_dates?source=${encodeURIComponent(
        source
      )}&limit=${CLOSING_DATES_LIMIT}&offset=${existing.length}`
    );
    if ('error' in response) return;

    const dates = response.closingDates ?? [];
    set(closingDatesAtom, [...existing, ...dates]);
    set(closingDatesHasMoreAtom, dates.length >= CLOSING_DATES_LIMIT);
  } catch {
    // silently fail, user can scroll again
  }
});

const stripPriceController = { current: new AbortController() };

export const fetchStripPriceDataAtom = atom(
  null,
  async (_get, set, date: string) => {
    stripPriceController.current.abort();
    stripPriceController.current = new AbortController();

    set(stripPriceLoadingAtom, true);
    try {
      const response = await request.post<StripPriceData[], unknown>(
        '/queries/strip_prices',
        { closingDates: [date] },
        stripPriceController.current
      );

      if (response.type === 'success' && response.data.length > 0) {
        set(stripPriceDataAtom, response.data[0]);
      }
    } finally {
      set(stripPriceLoadingAtom, false);
    }
  }
);
